'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';

// Enhanced features
import {
  testDNSResolution,
  detectProtocol,
  detectNAT,
  testGeographicRouting,
  saveTestResult,
  getHistoryForTarget,
  clearTestHistory,
  type DNSTest,
  type ProtocolInfo,
  type NATInfo,
  type GeoTest,
  type HistoricalTest
} from './enhanced-features';

// Advanced features
import {
  detectCDN,
  analyzeTLS,
  testIPVersions,
  detectMTU,
  calculateMOS,
  testConnectionStability,
  scanPorts,
  detectProxy,
  detectQoS,
  compareEndpoints,
  type CDNInfo,
  type TLSInfo,
  type IPVersionTest,
  type MTUInfo,
  type NetworkQuality,
  type StabilityTest,
  type PortScanResult,
  type ProxyInfo,
  type QoSInfo,
  type ComparativeResult
} from './advanced-features';

// Monitoring
import {
  ConnectionMonitor,
  calculateSLAStatus,
  AlertEngine,
  type MonitorConfig,
  type MonitorStats,
  type MonitorEvent,
  type SLAConfig,
  type SLAStatus,
  type AlertRule,
  type AlertCondition
} from './monitoring';

// Test result types
interface LatencyTest {
  url: string;
  method: 'HTTP' | 'WebSocket';
  min: number;
  max: number;
  avg: number;
  median: number;
  jitter: number;
  samples: number[];
  dns?: number;
  tcp?: number;
  tls?: number;
  ttfb?: number;
}

interface WebRTCTest {
  packetsLost: number;
  packetsSent: number;
  packetsReceived: number;
  lossRate: number;
  jitter: number;
  rtt: number;
  bitrate: number;
  codec?: string;
}

interface BandwidthTest {
  downloadSpeed: number;
  uploadSpeed: number;
  latency: number;
}

interface ComprehensiveTestResults {
  timestamp: Date;
  target: string;
  testMode: TestMode;

  // Basic tests
  latency?: LatencyTest;
  webrtc?: WebRTCTest;
  bandwidth?: BandwidthTest;

  // Enhanced features
  dns?: DNSTest[];
  protocol?: ProtocolInfo;
  nat?: NATInfo;
  geo?: GeoTest[];

  // Advanced features
  cdn?: CDNInfo;
  tls?: TLSInfo;
  ipVersions?: IPVersionTest;
  mtu?: MTUInfo;
  networkQuality?: NetworkQuality;
  stability?: StabilityTest;
  ports?: PortScanResult[];
  proxy?: ProxyInfo;
  qos?: QoSInfo;
  comparative?: ComparativeResult;

  errors: string[];
}

type TestMode =
  | 'quick'           // Latency + basic
  | 'standard'        // All basic tests
  | 'security'        // TLS, CDN, headers
  | 'performance'     // Stability, QoS, MTU, bandwidth
  | 'diagnostics'     // DNS, IPv6, NAT, proxy
  | 'full'            // Everything
  | 'monitor';        // Real-time monitoring

type ViewTab = 'test' | 'results' | 'monitor' | 'history' | 'report';

export default function ConnectivityTester() {
  const [target, setTarget] = useState('https://www.google.com');
  const [testMode, setTestMode] = useState<TestMode>('standard');
  const [activeTab, setActiveTab] = useState<ViewTab>('test');
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<ComprehensiveTestResults | null>(null);
  const [progress, setProgress] = useState('');
  const [history, setHistory] = useState<HistoricalTest[]>([]);
  const [skipWebRTC, setSkipWebRTC] = useState(false);

  // Monitoring state
  const [monitor, setMonitor] = useState<ConnectionMonitor | null>(null);
  const [monitorStats, setMonitorStats] = useState<MonitorStats | null>(null);
  const [monitorConfig, setMonitorConfig] = useState<MonitorConfig>({
    target: '',
    interval: 10,
    latencyThreshold: 500,
    packetLossThreshold: 5,
    notificationsEnabled: false
  });
  const [alertEngine] = useState(() => new AlertEngine((rule, message) => {
    console.log(`Alert: ${rule.name} - ${message}`);
  }));
  const [slaConfig, setSlaConfig] = useState<SLAConfig>({
    targetUptime: 99.9,
    targetLatency: 200,
    measurementPeriod: 'hour'
  });

  // Stability test state
  const [stabilityProgress, setStabilityProgress] = useState<{ timestamp: number; latency: number }[]>([]);

  const abortController = useRef<AbortController | null>(null);

  // Load history on mount
  useEffect(() => {
    if (target) {
      setHistory(getHistoryForTarget(target));
    }
  }, [target]);

  // Cleanup monitor on unmount
  useEffect(() => {
    return () => {
      if (monitor) {
        monitor.stop();
      }
    };
  }, [monitor]);

  // HTTP Latency Test
  const testHTTPLatency = async (url: string, samples: number = 10): Promise<LatencyTest> => {
    const timings: number[] = [];
    const performanceEntries: PerformanceResourceTiming[] = [];

    setProgress(`Testing HTTP latency (${samples} samples)...`);

    // Warm-up request
    try {
      await fetch(url, { method: 'HEAD', mode: 'no-cors', cache: 'no-store' });
    } catch (e) {
      // Ignore warm-up errors
    }

    // Collect samples
    for (let i = 0; i < samples; i++) {
      const start = performance.now();

      try {
        await fetch(url, {
          method: 'HEAD',
          mode: 'no-cors',
          cache: 'no-store',
          signal: abortController.current?.signal
        });

        const latency = performance.now() - start;
        timings.push(latency);

        // Try to get detailed timing
        const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        if (entries.length > 0) {
          performanceEntries.push(entries[entries.length - 1]);
        }
      } catch (error: any) {
        if (error.name === 'AbortError') {
          throw new Error('Test cancelled');
        }
        throw error;
      }

      setProgress(`Testing HTTP latency (${i + 1}/${samples})...`);
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Calculate statistics
    const sorted = [...timings].sort((a, b) => a - b);
    const avg = timings.reduce((a, b) => a + b, 0) / timings.length;
    const median = sorted[Math.floor(sorted.length / 2)];

    // Calculate jitter (average deviation from mean)
    const jitter = timings.reduce((sum, t) => sum + Math.abs(t - avg), 0) / timings.length;

    // Extract detailed timing from last entry
    let dns, tcp, tls, ttfb;
    if (performanceEntries.length > 0) {
      const entry = performanceEntries[performanceEntries.length - 1];
      dns = entry.domainLookupEnd - entry.domainLookupStart;
      tcp = entry.connectEnd - entry.connectStart;
      tls = entry.secureConnectionStart > 0 ? entry.requestStart - entry.secureConnectionStart : undefined;
      ttfb = entry.responseStart - entry.requestStart;
    }

    return {
      url,
      method: 'HTTP',
      min: Math.min(...timings),
      max: Math.max(...timings),
      avg,
      median,
      jitter,
      samples: timings,
      dns,
      tcp,
      tls,
      ttfb
    };
  };

  // WebRTC Packet Loss Test
  const testWebRTCPacketLoss = async (): Promise<WebRTCTest> => {
    console.log('[WebRTC] Starting test');
    setProgress('Testing WebRTC packet loss...');

    return new Promise((resolve, reject) => {
      let resolved = false;
      let overallTimeout: NodeJS.Timeout | null = null;
      let channelTimeout: NodeJS.Timeout | null = null;
      let checkInterval: NodeJS.Timeout | null = null;

      const cleanup = (pc1?: RTCPeerConnection, pc2?: RTCPeerConnection) => {
        console.log('[WebRTC] Cleanup called');
        if (overallTimeout) clearTimeout(overallTimeout);
        if (channelTimeout) clearTimeout(channelTimeout);
        if (checkInterval) clearInterval(checkInterval);
        try {
          if (pc1) pc1.close();
          if (pc2) pc2.close();
        } catch (e) {
          // Ignore cleanup errors
        }
      };

      let pc1: RTCPeerConnection;
      let pc2: RTCPeerConnection;

      try {
        console.log('[WebRTC] Creating peer connections');
        pc1 = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        });
        pc2 = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        });
        console.log('[WebRTC] Peer connections created');
      } catch (error: any) {
        console.error('[WebRTC] Failed to create RTCPeerConnection:', error);
        cleanup();
        reject(new Error('Failed to create RTCPeerConnection: ' + error.message));
        return;
      }

      const dataChannel = pc1.createDataChannel('test', {
        ordered: false,
        maxRetransmits: 0
      });
      let packetsReceived = 0;
      let channelOpened = false;

      // Set overall timeout (15s instead of 30s)
      overallTimeout = setTimeout(() => {
        console.log('[WebRTC] Overall timeout fired, resolved:', resolved);
        if (!resolved) {
          resolved = true;
          cleanup(pc1, pc2);
          reject(new Error('WebRTC test timeout (15s) - test took too long'));
        }
      }, 15000);

      // Timeout for channel opening (5s instead of 10s)
      channelTimeout = setTimeout(() => {
        console.log('[WebRTC] Channel timeout fired, channelOpened:', channelOpened, 'resolved:', resolved);
        if (!channelOpened && !resolved) {
          resolved = true;
          cleanup(pc1, pc2);
          reject(new Error('WebRTC data channel failed to open within 5 seconds'));
        }
      }, 5000);

      pc2.ondatachannel = (event) => {
        event.channel.onmessage = () => {
          packetsReceived++;
        };
      };

      // Monitor data channel state
      dataChannel.onopen = () => {
        console.log('[WebRTC] Data channel opened');
        channelOpened = true;
        if (channelTimeout) clearTimeout(channelTimeout);
        setProgress('WebRTC channel opened, sending packets...');
      };

      dataChannel.onerror = (error) => {
        console.error('[WebRTC] Data channel error:', error);
        if (!resolved) {
          resolved = true;
          cleanup(pc1, pc2);
          reject(new Error('WebRTC data channel error'));
        }
      };

      pc1.onicecandidate = (e) => {
        if (e.candidate) {
          console.log('[WebRTC] pc1 ICE candidate');
          pc2.addIceCandidate(e.candidate).catch(() => {});
        }
      };

      pc2.onicecandidate = (e) => {
        if (e.candidate) {
          console.log('[WebRTC] pc2 ICE candidate');
          pc1.addIceCandidate(e.candidate).catch(() => {});
        }
      };

      // Monitor connection state
      pc1.onconnectionstatechange = () => {
        console.log('[WebRTC] Connection state:', pc1.connectionState);
        if ((pc1.connectionState === 'failed' || pc1.connectionState === 'disconnected') && !resolved) {
          resolved = true;
          cleanup(pc1, pc2);
          reject(new Error(`WebRTC connection ${pc1.connectionState}`));
        }
      };

      // Start the connection process
      console.log('[WebRTC] Starting offer/answer exchange');
      pc1.createOffer()
        .then(offer => pc1.setLocalDescription(offer))
        .then(() => {
          if (!pc1.localDescription) throw new Error('No local description');
          return pc2.setRemoteDescription(pc1.localDescription);
        })
        .then(() => pc2.createAnswer())
        .then(answer => pc2.setLocalDescription(answer))
        .then(() => {
          if (!pc2.localDescription) throw new Error('No remote description');
          return pc1.setRemoteDescription(pc2.localDescription);
        })
        .then(() => {
          // Wait for data channel to open
          checkInterval = setInterval(() => {
            if (resolved) {
              if (checkInterval) clearInterval(checkInterval);
              return;
            }

            if (channelOpened && dataChannel.readyState === 'open') {
              if (checkInterval) clearInterval(checkInterval);

              // Send packets
              const packetsSent = 50; // Reduced from 100
              try {
                for (let i = 0; i < packetsSent; i++) {
                  dataChannel.send(`p${i}`);
                }
              } catch (error: any) {
                if (!resolved) {
                  resolved = true;
                  cleanup(pc1, pc2);
                  reject(new Error('Failed to send packets: ' + error.message));
                }
                return;
              }

              // Wait for packets to be received, then get stats
              setTimeout(async () => {
                if (resolved) return;

                try {
                  const stats = await pc1.getStats();
                  let packetsLost = 0;
                  let jitter = 0;
                  let rtt = 0;
                  let bitrate = 0;
                  let codec: string | undefined;

                  stats.forEach((report: any) => {
                    if (report.type === 'outbound-rtp') {
                      packetsLost = report.packetsLost || 0;
                      jitter = report.jitter || 0;
                      if (report.bytesSent && report.timestamp) {
                        bitrate = (report.bytesSent / report.timestamp) * 8;
                      }
                    }
                    if (report.type === 'candidate-pair' && report.state === 'succeeded') {
                      rtt = (report.currentRoundTripTime || 0) * 1000;
                    }
                    if (report.type === 'codec') {
                      codec = report.mimeType;
                    }
                  });

                  const lossRate = ((packetsSent - packetsReceived) / packetsSent) * 100;

                  if (!resolved) {
                    resolved = true;
                    cleanup(pc1, pc2);
                    resolve({
                      packetsLost,
                      packetsSent,
                      packetsReceived,
                      lossRate,
                      jitter,
                      rtt,
                      bitrate,
                      codec
                    });
                  }
                } catch (error: any) {
                  if (!resolved) {
                    resolved = true;
                    cleanup(pc1, pc2);
                    reject(new Error('Failed to get stats: ' + error.message));
                  }
                }
              }, 1500); // Reduced from 3000
            }
          }, 100);
        })
        .catch((error: any) => {
          if (!resolved) {
            resolved = true;
            cleanup(pc1, pc2);
            reject(new Error('WebRTC setup failed: ' + error.message));
          }
        });
    });
  };

  // Bandwidth Test
  const testBandwidth = async (url: string = 'https://speed.cloudflare.com/__down?bytes=10000000'): Promise<BandwidthTest> => {
    setProgress('Testing bandwidth...');

    try {
      // Download test
      const downloadStart = performance.now();
      const response = await fetch(url, {
        cache: 'no-store',
        signal: AbortSignal.timeout(30000) // 30s timeout for 10MB download
      });
      const blob = await response.blob();
      const downloadTime = (performance.now() - downloadStart) / 1000; // seconds

      const downloadSpeed = (blob.size * 8) / downloadTime / 1000000; // Mbps

      // Upload test (limited - most servers don't support it)
      const uploadSpeed = 0; // Placeholder

      // Latency
      const latencyStart = performance.now();
      await fetch(url, {
        method: 'HEAD',
        cache: 'no-store',
        signal: AbortSignal.timeout(10000) // 10s timeout for latency test
      });
      const latency = performance.now() - latencyStart;

      return {
        downloadSpeed,
        uploadSpeed,
        latency
      };
    } catch (error: any) {
      if (error.name === 'TimeoutError' || error.name === 'AbortError') {
        throw new Error('Bandwidth test timeout - network too slow or unavailable');
      }
      throw new Error(`Bandwidth test failed: ${error.message}`);
    }
  };

  // Main test runner
  const runTests = async () => {
    if (!target) {
      alert('Please enter a target URL');
      return;
    }

    setIsRunning(true);
    setProgress('Initializing tests...');
    setResults(null);
    setStabilityProgress([]);

    abortController.current = new AbortController();

    const testResults: ComprehensiveTestResults = {
      timestamp: new Date(),
      target,
      testMode,
      errors: []
    };

    try {
      // Quick mode: Latency only
      if (testMode === 'quick' || testMode === 'standard' || testMode === 'performance' || testMode === 'full') {
        try {
          testResults.latency = await testHTTPLatency(target, testMode === 'quick' ? 5 : 10);
        } catch (error: any) {
          testResults.errors.push(`Latency test failed: ${error.message}`);
        }
      }

      // Standard and above: WebRTC, DNS, Protocol
      if (testMode === 'standard' || testMode === 'diagnostics' || testMode === 'full') {
        // WebRTC test - skip if disabled or if it fails/times out
        if (!skipWebRTC) {
          try {
            setProgress('Testing WebRTC packet loss (max 15s timeout)...');
            const webrtcPromise = testWebRTCPacketLoss();
            const timeoutPromise = new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('WebRTC test exceeded 15s - skipping')), 15000)
            );
            testResults.webrtc = await Promise.race([webrtcPromise, timeoutPromise]);
          } catch (error: any) {
            console.warn('WebRTC test failed or timed out:', error.message);
            testResults.errors.push(`WebRTC test skipped: ${error.message}`);
          }
        } else {
          testResults.errors.push('WebRTC test disabled by user');
        }

        try {
          testResults.dns = await testDNSResolution(target);
        } catch (error: any) {
          testResults.errors.push(`DNS test failed: ${error.message}`);
        }

        try {
          const protocol = await detectProtocol(target);
          if (protocol) testResults.protocol = protocol;
        } catch (error: any) {
          testResults.errors.push(`Protocol detection failed: ${error.message}`);
        }
      }

      // Security mode: CDN, TLS
      if (testMode === 'security' || testMode === 'full') {
        try {
          testResults.cdn = await detectCDN(target);
        } catch (error: any) {
          testResults.errors.push(`CDN detection failed: ${error.message}`);
        }

        try {
          testResults.tls = await analyzeTLS(target);
        } catch (error: any) {
          testResults.errors.push(`TLS analysis failed: ${error.message}`);
        }
      }

      // Performance mode: Bandwidth, Stability, QoS, MTU
      if (testMode === 'performance' || testMode === 'full') {
        try {
          testResults.bandwidth = await testBandwidth();
        } catch (error: any) {
          testResults.errors.push(`Bandwidth test failed: ${error.message}`);
        }

        try {
          testResults.qos = await detectQoS(target);
        } catch (error: any) {
          testResults.errors.push(`QoS detection failed: ${error.message}`);
        }

        try {
          testResults.mtu = await detectMTU(target);
        } catch (error: any) {
          testResults.errors.push(`MTU detection failed: ${error.message}`);
        }

        // Stability test (shorter for UI responsiveness)
        try {
          testResults.stability = await testConnectionStability(target, 30, (sample) => {
            setStabilityProgress(prev => [...prev, sample]);
          }, abortController.current?.signal);
        } catch (error: any) {
          testResults.errors.push(`Stability test failed: ${error.message}`);
        }
      }

      // Diagnostics mode: NAT, IPv6, Proxy, Ports
      if (testMode === 'diagnostics' || testMode === 'full') {
        try {
          testResults.nat = await detectNAT();
        } catch (error: any) {
          testResults.errors.push(`NAT detection failed: ${error.message}`);
        }

        try {
          testResults.ipVersions = await testIPVersions(target);
        } catch (error: any) {
          testResults.errors.push(`IPv6 test failed: ${error.message}`);
        }

        try {
          const nat = testResults.nat || await detectNAT();
          testResults.proxy = await detectProxy(nat.localIPs, nat.publicIP);
        } catch (error: any) {
          testResults.errors.push(`Proxy detection failed: ${error.message}`);
        }

        try {
          testResults.ports = await scanPorts(target);
        } catch (error: any) {
          testResults.errors.push(`Port scan failed: ${error.message}`);
        }
      }

      // Full mode: Geographic, Comparative
      if (testMode === 'full') {
        try {
          testResults.geo = await testGeographicRouting(target);
        } catch (error: any) {
          testResults.errors.push(`Geographic test failed: ${error.message}`);
        }

        try {
          testResults.comparative = await compareEndpoints(target);
        } catch (error: any) {
          testResults.errors.push(`Comparative analysis failed: ${error.message}`);
        }
      }

      // Calculate network quality (MOS score)
      if (testResults.latency && testResults.webrtc) {
        testResults.networkQuality = calculateMOS(
          testResults.latency.avg,
          testResults.latency.jitter,
          testResults.webrtc.lossRate
        );
      }

      setResults(testResults);
      setActiveTab('results');

      // Save to history
      saveTestResult({
        id: Date.now().toString(),
        timestamp: Date.now(),
        target,
        latency: testResults.latency?.avg,
        packetLoss: testResults.webrtc?.lossRate,
        bandwidth: testResults.bandwidth?.downloadSpeed
      });

      setProgress('Tests completed!');
    } catch (error: any) {
      testResults.errors.push(`Test failed: ${error.message}`);
      setResults(testResults);
    } finally {
      setIsRunning(false);
      abortController.current = null;
    }
  };

  const cancelTests = () => {
    if (abortController.current) {
      abortController.current.abort();
      setIsRunning(false);
      setProgress('Tests cancelled');
    }
  };

  // Monitoring functions
  const startMonitoring = () => {
    if (!target) {
      alert('Please enter a target URL to monitor');
      return;
    }

    const config = { ...monitorConfig, target };
    const mon = new ConnectionMonitor(
      config,
      (stats) => {
        setMonitorStats(stats);

        // Evaluate alert rules
        if (stats.events.length > 0) {
          const latestEvent = stats.events[0];
          alertEngine.evaluate(stats, latestEvent.latency, latestEvent.packetLoss);
        }
      },
      (event) => {
        console.log('Monitor alert:', event);
      }
    );

    mon.start();
    setMonitor(mon);
  };

  const stopMonitoring = () => {
    if (monitor) {
      monitor.stop();
      setMonitor(null);
    }
  };

  const exportMonitorReport = () => {
    if (monitor) {
      const report = monitor.exportStats();
      downloadFile(report, 'monitor-report.md', 'text/markdown');
    }
  };

  // Export functions
  const exportJSON = () => {
    if (!results) return;
    const json = JSON.stringify(results, null, 2);
    downloadFile(json, 'connectivity-test.json', 'application/json');
  };

  const exportMarkdown = () => {
    if (!results) return;
    const md = generateMarkdownReport(results);
    downloadFile(md, 'connectivity-test.md', 'text/markdown');
  };

  const downloadFile = (content: string, filename: string, type: string) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generateMarkdownReport = (results: ComprehensiveTestResults): string => {
    let md = `# Network Connectivity Test Report\n\n`;
    md += `**Target**: ${results.target}\n`;
    md += `**Test Mode**: ${results.testMode}\n`;
    md += `**Timestamp**: ${results.timestamp.toISOString()}\n\n`;

    if (results.networkQuality) {
      md += `## Overall Quality\n\n`;
      md += `- **MOS Score**: ${results.networkQuality.mosScore}/5.0 (${results.networkQuality.rating})\n`;
      md += `- **VoIP Quality**: ${results.networkQuality.voipQuality}\n`;
      md += `- **Video Quality**: ${results.networkQuality.videoQuality}\n\n`;
    }

    if (results.tls) {
      md += `## Security\n\n`;
      md += `- **Security Grade**: ${results.tls.securityGrade || 'Unknown'}\n`;
      md += `- **HTTPS**: ${results.tls.secureConnection ? 'Yes' : 'No'}\n`;
      md += `- **HSTS**: ${results.tls.hsts ? 'Enabled' : 'Disabled'}\n\n`;
    }

    if (results.latency) {
      md += `## Latency\n\n`;
      md += `- **Average**: ${results.latency.avg.toFixed(2)} ms\n`;
      md += `- **Minimum**: ${results.latency.min.toFixed(2)} ms\n`;
      md += `- **Maximum**: ${results.latency.max.toFixed(2)} ms\n`;
      md += `- **Jitter**: ${results.latency.jitter.toFixed(2)} ms\n\n`;
    }

    if (results.bandwidth) {
      md += `## Bandwidth\n\n`;
      md += `- **Download**: ${results.bandwidth.downloadSpeed.toFixed(2)} Mbps\n`;
      md += `- **Upload**: ${results.bandwidth.uploadSpeed.toFixed(2)} Mbps\n\n`;
    }

    if (results.cdn) {
      md += `## CDN Detection\n\n`;
      md += `- **CDN**: ${results.cdn.isCDN ? results.cdn.provider : 'None detected'}\n`;
      if (results.cdn.cacheStatus) {
        md += `- **Cache Status**: ${results.cdn.cacheStatus}\n`;
      }
      md += `\n`;
    }

    if (results.dns && results.dns.length > 0) {
      md += `## DNS Resolution\n\n`;
      results.dns.forEach(dns => {
        md += `- **${dns.resolver}**: ${dns.time.toFixed(2)} ms`;
        if (dns.ip) md += ` → ${dns.ip}`;
        md += `\n`;
      });
      md += `\n`;
    }

    if (results.errors.length > 0) {
      md += `## Errors\n\n`;
      results.errors.forEach(err => {
        md += `- ${err}\n`;
      });
    }

    return md;
  };

  return (
    <div className="min-h-screen bg-light dark:bg-background text-accent-foreground dark:text-foreground py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <Link href="/tools" className="text-accent hover:underline mb-4 inline-block">
            ← Back to Tools
          </Link>
          <h1 className="text-4xl font-bold mb-4">Network Connectivity Tester</h1>
          <p className="text-lg opacity-80">
            Comprehensive network diagnostics: latency, bandwidth, DNS, security, NAT, and more
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-navy/20 dark:border-border">
          {(['test', 'results', 'monitor', 'history', 'report'] as ViewTab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium transition-colors capitalize ${
                activeTab === tab
                  ? 'border-b-2 border-accent text-accent'
                  : 'text-accent-foreground/60 dark:text-foreground-tertiary hover:text-accent-foreground dark:hover:text-foreground'
              }`}
            >
              {tab}
              {tab === 'history' && history.length > 0 && ` (${history.length})`}
              {tab === 'monitor' && monitor?.isActive() && ' 🔴'}
            </button>
          ))}
        </div>

        {/* Test Tab */}
        {activeTab === 'test' && (
          <div className="space-y-6">
            {/* Configuration */}
            <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
              <h2 className="text-2xl font-bold mb-4">Test Configuration</h2>

              <div className="space-y-4">
                <div>
                  <label className="block mb-2 font-medium">Target URL or Domain</label>
                  <input
                    type="text"
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    className="w-full px-4 py-2 bg-white dark:bg-background text-accent-foreground dark:text-foreground border border-navy/20 dark:border-border rounded"
                    placeholder="https://www.example.com"
                    disabled={isRunning}
                  />
                </div>

                <div>
                  <label className="block mb-2 font-medium">Test Mode</label>
                  <select
                    value={testMode}
                    onChange={(e) => setTestMode(e.target.value as TestMode)}
                    className="w-full px-4 py-2 bg-white dark:bg-background text-accent-foreground dark:text-foreground border border-navy/20 dark:border-border rounded"
                    disabled={isRunning}
                  >
                    <option value="quick">Quick Test (5s) - Latency only</option>
                    <option value="standard">Standard Test (30s) - Latency, DNS, WebRTC</option>
                    <option value="security">Security Audit (20s) - TLS, CDN, Headers</option>
                    <option value="performance">Performance Deep Dive (60s) - Bandwidth, Stability, QoS</option>
                    <option value="diagnostics">Network Diagnostics (40s) - DNS, IPv6, NAT, Ports</option>
                    <option value="full">Full Scan (120s) - Everything</option>
                  </select>

                  <div className="mt-2 text-sm opacity-70">
                    {testMode === 'quick' && '⚡ Fast latency check'}
                    {testMode === 'standard' && '📊 Comprehensive connectivity test'}
                    {testMode === 'security' && '🔒 Security-focused analysis'}
                    {testMode === 'performance' && '🚀 Deep performance analysis'}
                    {testMode === 'diagnostics' && '🔧 Advanced network diagnostics'}
                    {testMode === 'full' && '🔬 Complete diagnostic scan'}
                  </div>
                </div>

                {/* WebRTC Skip Option */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="skipWebRTC"
                    checked={skipWebRTC}
                    onChange={(e) => setSkipWebRTC(e.target.checked)}
                    className="w-4 h-4 rounded"
                    disabled={isRunning}
                  />
                  <label htmlFor="skipWebRTC" className="text-sm cursor-pointer">
                    Skip WebRTC test (browser capability test, not endpoint-specific)
                  </label>
                </div>

                <div className="flex gap-4">
                  {!isRunning ? (
                    <button
                      onClick={runTests}
                      className="px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent-hover transition-colors font-medium"
                    >
                      Run Tests
                    </button>
                  ) : (
                    <button
                      onClick={cancelTests}
                      className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                    >
                      Cancel Tests
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Progress */}
            {isRunning && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="animate-spin h-5 w-5 border-2 border-accent border-t-transparent rounded-full"></div>
                  <span className="font-medium">{progress}</span>
                </div>

                {stabilityProgress.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm opacity-70 mb-2">
                      Stability Test Progress ({stabilityProgress.length} samples)
                    </div>
                    <div className="h-32 bg-white dark:bg-background rounded border border-navy/20 dark:border-border p-2">
                      <svg width="100%" height="100%" viewBox="0 0 400 100" preserveAspectRatio="none">
                        <polyline
                          points={stabilityProgress.map((p, i) =>
                            `${(i / stabilityProgress.length) * 400},${100 - (p.latency / Math.max(...stabilityProgress.map(s => s.latency)) * 80)}`
                          ).join(' ')}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="text-accent"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Test Mode Descriptions */}
            <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
              <h3 className="text-xl font-bold mb-4">Test Modes Explained</h3>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-navy/20 dark:border-border rounded p-4">
                  <div className="font-medium mb-2">🔒 Security Audit</div>
                  <ul className="text-sm opacity-80 space-y-1 list-disc list-inside">
                    <li>TLS/SSL analysis with security grade</li>
                    <li>CDN and cache detection</li>
                    <li>Security headers check</li>
                    <li>Certificate validation</li>
                  </ul>
                </div>

                <div className="border border-navy/20 dark:border-border rounded p-4">
                  <div className="font-medium mb-2">🚀 Performance Deep Dive</div>
                  <ul className="text-sm opacity-80 space-y-1 list-disc list-inside">
                    <li>Connection stability (30s)</li>
                    <li>QoS and throttling detection</li>
                    <li>MTU and fragmentation</li>
                    <li>Bandwidth testing</li>
                  </ul>
                </div>

                <div className="border border-navy/20 dark:border-border rounded p-4">
                  <div className="font-medium mb-2">🔧 Network Diagnostics</div>
                  <ul className="text-sm opacity-80 space-y-1 list-disc list-inside">
                    <li>DNS resolution (3 providers)</li>
                    <li>IPv4 vs IPv6 comparison</li>
                    <li>NAT and topology detection</li>
                    <li>Proxy/VPN detection</li>
                    <li>Port scanning</li>
                  </ul>
                </div>

                <div className="border border-navy/20 dark:border-border rounded p-4">
                  <div className="font-medium mb-2">🔬 Full Scan</div>
                  <ul className="text-sm opacity-80 space-y-1 list-disc list-inside">
                    <li>All tests from other modes</li>
                    <li>Geographic routing analysis</li>
                    <li>Comparative benchmarking</li>
                    <li>MOS quality score</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Tab */}
        {activeTab === 'results' && results && (
          <div className="space-y-6">
            {/* Summary Card */}
            <div className="bg-gradient-to-r from-accent-muted to-accent/5 dark:from-accent-muted dark:to-accent/10 p-6 rounded-lg border border-border">
              <h2 className="text-2xl font-bold mb-4">Test Summary</h2>

              <div className="grid md:grid-cols-3 gap-6">
                {results.networkQuality && (
                  <div>
                    <div className="text-sm opacity-70 mb-1">Network Quality (MOS)</div>
                    <div className="flex items-baseline gap-2">
                      <div className="text-3xl font-bold text-accent">
                        {results.networkQuality.mosScore}
                      </div>
                      <div className="text-sm">/5.0</div>
                    </div>
                    <div className={`text-sm font-medium mt-1 ${
                      results.networkQuality.rating === 'Excellent' ? 'text-green-600 dark:text-green-400' :
                      results.networkQuality.rating === 'Good' ? 'text-blue-600 dark:text-blue-400' :
                      results.networkQuality.rating === 'Fair' ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-red-600 dark:text-red-400'
                    }`}>
                      {results.networkQuality.rating}
                    </div>
                  </div>
                )}

                {results.tls && (
                  <div>
                    <div className="text-sm opacity-70 mb-1">Security Grade</div>
                    <div className={`text-3xl font-bold ${
                      results.tls.securityGrade === 'A+' || results.tls.securityGrade === 'A' ? 'text-green-600 dark:text-green-400' :
                      results.tls.securityGrade === 'B' ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-red-600 dark:text-red-400'
                    }`}>
                      {results.tls.securityGrade || 'N/A'}
                    </div>
                    <div className="text-sm mt-1">
                      {results.tls.secureConnection ? '🔒 HTTPS' : '⚠️ HTTP'}
                    </div>
                  </div>
                )}

                {results.latency && (
                  <div>
                    <div className="text-sm opacity-70 mb-1">Average Latency</div>
                    <div className="flex items-baseline gap-2">
                      <div className="text-3xl font-bold">
                        {results.latency.avg.toFixed(0)}
                      </div>
                      <div className="text-sm">ms</div>
                    </div>
                    <div className={`text-sm font-medium mt-1 ${
                      results.latency.avg < 50 ? 'text-green-600 dark:text-green-400' :
                      results.latency.avg < 150 ? 'text-blue-600 dark:text-blue-400' :
                      results.latency.avg < 300 ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-red-600 dark:text-red-400'
                    }`}>
                      {results.latency.avg < 50 ? 'Excellent' :
                       results.latency.avg < 150 ? 'Good' :
                       results.latency.avg < 300 ? 'Fair' : 'Poor'}
                    </div>
                  </div>
                )}
              </div>

              {results.comparative && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="text-sm opacity-70 mb-2">Compared to {results.comparative.baseline}</div>
                  <div className={`font-medium ${
                    results.comparative.verdict === 'Much Faster' || results.comparative.verdict === 'Faster' ? 'text-green-600 dark:text-green-400' :
                    results.comparative.verdict === 'Similar' ? 'text-blue-600 dark:text-blue-400' :
                    'text-red-600 dark:text-red-400'
                  }`}>
                    {results.comparative.verdict} ({results.comparative.relativeDifference > 0 ? '+' : ''}{results.comparative.relativeDifference.toFixed(1)}%)
                  </div>
                </div>
              )}
            </div>

            {/* Latency Details */}
            {results.latency && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Latency Analysis</h3>

                <div className="grid md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-sm opacity-70">Average</div>
                    <div className="text-2xl font-bold">{results.latency.avg.toFixed(2)} ms</div>
                  </div>
                  <div>
                    <div className="text-sm opacity-70">Minimum</div>
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">{results.latency.min.toFixed(2)} ms</div>
                  </div>
                  <div>
                    <div className="text-sm opacity-70">Maximum</div>
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">{results.latency.max.toFixed(2)} ms</div>
                  </div>
                  <div>
                    <div className="text-sm opacity-70">Jitter</div>
                    <div className="text-2xl font-bold">{results.latency.jitter.toFixed(2)} ms</div>
                  </div>
                </div>

                {(results.latency.dns || results.latency.tcp || results.latency.tls || results.latency.ttfb) && (
                  <div className="border-t border-navy/20 dark:border-border pt-4">
                    <div className="text-sm font-medium mb-2">Timing Breakdown</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {results.latency.dns !== undefined && (
                        <div className="text-sm">
                          <span className="opacity-70">DNS:</span> {results.latency.dns.toFixed(2)} ms
                        </div>
                      )}
                      {results.latency.tcp !== undefined && (
                        <div className="text-sm">
                          <span className="opacity-70">TCP:</span> {results.latency.tcp.toFixed(2)} ms
                        </div>
                      )}
                      {results.latency.tls !== undefined && (
                        <div className="text-sm">
                          <span className="opacity-70">TLS:</span> {results.latency.tls.toFixed(2)} ms
                        </div>
                      )}
                      {results.latency.ttfb !== undefined && (
                        <div className="text-sm">
                          <span className="opacity-70">TTFB:</span> {results.latency.ttfb.toFixed(2)} ms
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* WebRTC / Packet Loss */}
            {results.webrtc && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">WebRTC / Packet Loss</h3>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm opacity-70">Packet Loss Rate</div>
                    <div className={`text-2xl font-bold ${
                      results.webrtc.lossRate < 1 ? 'text-green-600 dark:text-green-400' :
                      results.webrtc.lossRate < 5 ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-red-600 dark:text-red-400'
                    }`}>
                      {results.webrtc.lossRate.toFixed(2)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-sm opacity-70">Jitter</div>
                    <div className="text-2xl font-bold">{results.webrtc.jitter.toFixed(2)} ms</div>
                  </div>
                  <div>
                    <div className="text-sm opacity-70">Round Trip Time</div>
                    <div className="text-2xl font-bold">{results.webrtc.rtt.toFixed(2)} ms</div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <div>
                    <span className="opacity-70">Packets Sent:</span> {results.webrtc.packetsSent}
                  </div>
                  <div>
                    <span className="opacity-70">Packets Received:</span> {results.webrtc.packetsReceived}
                  </div>
                  <div>
                    <span className="opacity-70">Packets Lost:</span> {results.webrtc.packetsLost}
                  </div>
                </div>
              </div>
            )}

            {/* Network Quality (MOS) */}
            {results.networkQuality && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Network Quality Assessment (MOS)</h3>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm opacity-70">Quality Score</span>
                    <span className="font-bold">{results.networkQuality.mosScore} / 5.0</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all ${
                        results.networkQuality.mosScore >= 4.3 ? 'bg-green-500' :
                        results.networkQuality.mosScore >= 4.0 ? 'bg-blue-500' :
                        results.networkQuality.mosScore >= 3.6 ? 'bg-yellow-500' :
                        results.networkQuality.mosScore >= 3.0 ? 'bg-accent-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${(results.networkQuality.mosScore / 5) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border border-navy/20 dark:border-border rounded p-3">
                    <div className="text-sm opacity-70 mb-1">VoIP Quality</div>
                    <div className="font-medium">{results.networkQuality.voipQuality}</div>
                  </div>
                  <div className="border border-navy/20 dark:border-border rounded p-3">
                    <div className="text-sm opacity-70 mb-1">Video Quality</div>
                    <div className="font-medium">{results.networkQuality.videoQuality}</div>
                  </div>
                </div>

                <div className="mt-4 text-sm opacity-70">
                  Based on ITU-T G.107 E-Model calculation using latency ({results.networkQuality.metrics.latency.toFixed(0)}ms),
                  jitter ({results.networkQuality.metrics.jitter.toFixed(0)}ms), and packet loss ({results.networkQuality.metrics.packetLoss.toFixed(1)}%)
                </div>
              </div>
            )}

            {/* Bandwidth */}
            {results.bandwidth && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Bandwidth</h3>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm opacity-70">Download Speed</div>
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {results.bandwidth.downloadSpeed.toFixed(2)} Mbps
                    </div>
                  </div>
                  <div>
                    <div className="text-sm opacity-70">Upload Speed</div>
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {results.bandwidth.uploadSpeed.toFixed(2)} Mbps
                    </div>
                  </div>
                  <div>
                    <div className="text-sm opacity-70">Test Latency</div>
                    <div className="text-2xl font-bold">
                      {results.bandwidth.latency.toFixed(2)} ms
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* CDN Detection */}
            {results.cdn && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">CDN & Cache Detection</h3>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm opacity-70">CDN Detected:</span>
                    <span className={`font-medium ${results.cdn.isCDN ? 'text-green-600 dark:text-green-400' : ''}`}>
                      {results.cdn.isCDN ? results.cdn.provider : 'No CDN detected'}
                    </span>
                  </div>

                  {results.cdn.cacheStatus && (
                    <div className="flex items-center gap-3">
                      <span className="text-sm opacity-70">Cache Status:</span>
                      <span className={`px-3 py-1 rounded text-sm font-medium ${
                        results.cdn.cacheStatus === 'HIT' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                        results.cdn.cacheStatus === 'MISS' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                        'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200'
                      }`}>
                        {results.cdn.cacheStatus}
                      </span>
                    </div>
                  )}

                  {results.cdn.popLocation && (
                    <div className="flex items-center gap-3">
                      <span className="text-sm opacity-70">POP Location:</span>
                      <span className="font-medium">{results.cdn.popLocation}</span>
                    </div>
                  )}

                  {results.cdn.age !== undefined && (
                    <div className="flex items-center gap-3">
                      <span className="text-sm opacity-70">Cache Age:</span>
                      <span className="font-medium">{results.cdn.age} seconds</span>
                    </div>
                  )}

                  {results.cdn.server && (
                    <div className="flex items-center gap-3">
                      <span className="text-sm opacity-70">Server:</span>
                      <span className="font-mono text-sm">{results.cdn.server}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TLS/Security */}
            {results.tls && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">TLS/SSL Security Analysis</h3>

                <div className="mb-4">
                  <div className="flex items-center gap-4 mb-3">
                    <div>
                      <span className="text-sm opacity-70">Security Grade:</span>
                      <span className={`ml-2 text-2xl font-bold ${
                        results.tls.securityGrade === 'A+' || results.tls.securityGrade === 'A' ? 'text-green-600 dark:text-green-400' :
                        results.tls.securityGrade === 'B' ? 'text-yellow-600 dark:text-yellow-400' :
                        'text-red-600 dark:text-red-400'
                      }`}>
                        {results.tls.securityGrade}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm opacity-70">HTTPS:</span>
                      <span className="ml-2 font-medium">
                        {results.tls.secureConnection ? '✅ Yes' : '❌ No'}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm opacity-70">HSTS:</span>
                      <span className="ml-2 font-medium">
                        {results.tls.hsts ? '✅ Enabled' : '⚠️ Disabled'}
                      </span>
                    </div>
                  </div>

                  {results.tls.protocol && (
                    <div className="text-sm mb-2">
                      <span className="opacity-70">TLS Version:</span> {results.tls.protocol}
                    </div>
                  )}

                  {results.tls.hstsMaxAge && (
                    <div className="text-sm mb-2">
                      <span className="opacity-70">HSTS Max-Age:</span> {results.tls.hstsMaxAge} seconds
                      ({(results.tls.hstsMaxAge / 86400).toFixed(0)} days)
                    </div>
                  )}
                </div>

                {results.tls.warnings.length > 0 && (
                  <div className="border-t border-navy/20 dark:border-border pt-4">
                    <div className="text-sm font-medium mb-2">⚠️ Security Warnings</div>
                    <ul className="space-y-1 text-sm">
                      {results.tls.warnings.map((warning, i) => (
                        <li key={i} className="text-yellow-600 dark:text-yellow-400">• {warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* DNS Results */}
            {results.dns && results.dns.length > 0 && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">DNS Resolution Test</h3>

                <div className="space-y-2">
                  {results.dns.map((dns, i) => (
                    <div key={i} className="flex items-center justify-between border border-navy/20 dark:border-border rounded p-3">
                      <div>
                        <div className="font-medium">{dns.resolver}</div>
                        {dns.ip && <div className="text-sm opacity-70 font-mono">{dns.ip}</div>}
                        {dns.error && <div className="text-sm text-red-600 dark:text-red-400">{dns.error}</div>}
                      </div>
                      <div className={`text-lg font-bold ${
                        dns.time < 50 ? 'text-green-600 dark:text-green-400' :
                        dns.time < 100 ? 'text-yellow-600 dark:text-yellow-400' :
                        'text-red-600 dark:text-red-400'
                      }`}>
                        {dns.time.toFixed(2)} ms
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* IPv4 vs IPv6 */}
            {results.ipVersions && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">IPv4 vs IPv6 Comparison</h3>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className={`border rounded p-4 ${
                    results.ipVersions.ipv4Available
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-300 dark:border-gray-700'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium">IPv4</div>
                      <div className={results.ipVersions.ipv4Available ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                        {results.ipVersions.ipv4Available ? '✅ Available' : '❌ Not Available'}
                      </div>
                    </div>
                    {results.ipVersions.ipv4Latency && (
                      <div className="text-2xl font-bold">{results.ipVersions.ipv4Latency.toFixed(2)} ms</div>
                    )}
                  </div>

                  <div className={`border rounded p-4 ${
                    results.ipVersions.ipv6Available
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-300 dark:border-gray-700'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium">IPv6</div>
                      <div className={results.ipVersions.ipv6Available ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                        {results.ipVersions.ipv6Available ? '✅ Available' : '❌ Not Available'}
                      </div>
                    </div>
                    {results.ipVersions.ipv6Latency && (
                      <div className="text-2xl font-bold">{results.ipVersions.ipv6Latency.toFixed(2)} ms</div>
                    )}
                  </div>
                </div>

                <div className="text-sm">
                  <span className="opacity-70">Status:</span>
                  <span className="ml-2 font-medium">
                    {results.ipVersions.dualStack ? '✅ Dual-Stack (IPv4 + IPv6)' :
                     results.ipVersions.ipv6Available ? 'IPv6 Only' : 'IPv4 Only'}
                  </span>
                </div>

                {results.ipVersions.preferredVersion && (
                  <div className="text-sm mt-2">
                    <span className="opacity-70">Preferred Version:</span>
                    <span className="ml-2 font-medium">{results.ipVersions.preferredVersion}</span>
                  </div>
                )}
              </div>
            )}

            {/* NAT/Network Topology */}
            {results.nat && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Network Topology & NAT</h3>

                <div className="space-y-3">
                  {results.nat.natType && (
                    <div>
                      <span className="text-sm opacity-70">NAT Type:</span>
                      <span className="ml-2 font-medium">{results.nat.natType}</span>
                    </div>
                  )}

                  {results.nat.localIPs.length > 0 && (
                    <div>
                      <span className="text-sm opacity-70">Local IPs:</span>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {results.nat.localIPs.map((ip, i) => (
                          <span key={i} className="px-2 py-1 bg-white dark:bg-background rounded text-sm font-mono">
                            {ip}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {results.nat.publicIP && (
                    <div>
                      <span className="text-sm opacity-70">Public IP:</span>
                      <span className="ml-2 font-mono font-medium">{results.nat.publicIP}</span>
                    </div>
                  )}

                  {results.nat.iceCandidates.length > 0 && (
                    <div>
                      <details className="cursor-pointer">
                        <summary className="text-sm opacity-70">
                          ICE Candidates ({results.nat.iceCandidates.length})
                        </summary>
                        <div className="mt-2 space-y-1 text-sm max-h-40 overflow-y-auto">
                          {results.nat.iceCandidates.map((c, i) => (
                            <div key={i} className="font-mono text-xs">
                              {c.type} | {c.protocol} | {c.address}:{c.port}
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Proxy/VPN Detection */}
            {results.proxy && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Proxy/VPN Detection</h3>

                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <div>
                      <span className="text-sm opacity-70">Proxy Detected:</span>
                      <span className={`ml-2 font-medium ${results.proxy.likelyProxy ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                        {results.proxy.likelyProxy ? 'Likely Yes' : 'No'}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm opacity-70">VPN Detected:</span>
                      <span className={`ml-2 font-medium ${results.proxy.likelyVPN ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                        {results.proxy.likelyVPN ? 'Likely Yes' : 'No'}
                      </span>
                    </div>
                  </div>

                  {results.proxy.indicators.length > 0 && (
                    <div>
                      <div className="text-sm opacity-70 mb-2">Indicators:</div>
                      <ul className="space-y-1 text-sm">
                        {results.proxy.indicators.map((indicator, i) => (
                          <li key={i} className="text-yellow-600 dark:text-yellow-400">• {indicator}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-2 text-sm pt-2 border-t border-navy/20 dark:border-border">
                    <div>
                      <span className="opacity-70">IP Mismatch:</span> {results.proxy.ipMismatch ? '⚠️ Yes' : '✅ No'}
                    </div>
                    <div>
                      <span className="opacity-70">WebRTC Leak:</span> {results.proxy.webRTCLeak ? '⚠️ Yes' : '✅ No'}
                    </div>
                    <div>
                      <span className="opacity-70">DNS Leak:</span> {results.proxy.dnsLeak ? '⚠️ Yes' : '✅ No'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Port Scan */}
            {results.ports && results.ports.length > 0 && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Port Scan Results</h3>

                <div className="grid md:grid-cols-2 gap-2">
                  {results.ports.map((port, i) => (
                    <div key={i} className={`flex items-center justify-between border rounded p-2 text-sm ${
                      port.open
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-300 dark:border-gray-700'
                    }`}>
                      <div>
                        <span className="font-medium">{port.port}</span>
                        <span className="opacity-70 ml-2">({port.service})</span>
                      </div>
                      <div className={port.open ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                        {port.open ? 'Open' : 'Closed/Filtered'}
                        {port.latency && ` (${port.latency.toFixed(0)}ms)`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Connection Stability */}
            {results.stability && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Connection Stability Test</h3>

                <div className="grid md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-sm opacity-70">Duration</div>
                    <div className="text-2xl font-bold">{results.stability.duration}s</div>
                  </div>
                  <div>
                    <div className="text-sm opacity-70">Samples</div>
                    <div className="text-2xl font-bold">{results.stability.samples.length}</div>
                  </div>
                  <div>
                    <div className="text-sm opacity-70">Drops</div>
                    <div className={`text-2xl font-bold ${
                      results.stability.drops === 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      {results.stability.drops}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm opacity-70">Stability</div>
                    <div className={`text-2xl font-bold ${
                      results.stability.stability === 'Stable' ? 'text-green-600 dark:text-green-400' :
                      results.stability.stability === 'Moderate' ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-red-600 dark:text-red-400'
                    }`}>
                      {results.stability.stability}
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mb-4 text-sm">
                  <div>
                    <span className="opacity-70">Average:</span> {results.stability.avgLatency.toFixed(2)} ms
                  </div>
                  <div>
                    <span className="opacity-70">Min:</span> {results.stability.minLatency.toFixed(2)} ms
                  </div>
                  <div>
                    <span className="opacity-70">Max:</span> {results.stability.maxLatency.toFixed(2)} ms
                  </div>
                  <div>
                    <span className="opacity-70">Std Dev:</span> {results.stability.stdDev.toFixed(2)} ms
                  </div>
                </div>

                {/* Stability Chart */}
                <div className="h-48 bg-white dark:bg-background rounded border border-navy/20 dark:border-border p-4">
                  <svg width="100%" height="100%" viewBox="0 0 600 150" preserveAspectRatio="none">
                    {(() => {
                      const stability = results.stability;
                      if (!stability) return null;
                      return (
                        <polyline
                          points={stability.samples.map((s, i) =>
                            `${(i / stability.samples.length) * 600},${150 - ((s.latency - stability.minLatency) / (stability.maxLatency - stability.minLatency) * 120)}`
                          ).join(' ')}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="text-accent"
                        />
                      );
                    })()}
                  </svg>
                </div>
              </div>
            )}

            {/* QoS Detection */}
            {results.qos && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Quality of Service (QoS) Detection</h3>

                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <div>
                      <span className="text-sm opacity-70">Throttling Detected:</span>
                      <span className={`ml-2 font-medium ${results.qos.throttlingDetected ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                        {results.qos.throttlingDetected ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm opacity-70">Traffic Shaping:</span>
                      <span className={`ml-2 font-medium ${results.qos.shapingDetected ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                        {results.qos.shapingDetected ? 'Detected' : 'Not Detected'}
                      </span>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm opacity-70">Average Throughput</div>
                      <div className="text-2xl font-bold">{results.qos.avgThroughput.toFixed(2)} Mbps</div>
                    </div>
                    {results.qos.suspectedLimit && (
                      <div>
                        <div className="text-sm opacity-70">Suspected Limit</div>
                        <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                          {results.qos.suspectedLimit.toFixed(2)} Mbps
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* MTU Detection */}
            {results.mtu && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">MTU & Fragmentation Detection</h3>

                <div className="mb-4">
                  <div className="text-sm opacity-70">Estimated MTU</div>
                  <div className="text-2xl font-bold">{results.mtu.estimatedMTU} bytes</div>
                  {results.mtu.fragmentationDetected && (
                    <div className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                      ⚠️ Fragmentation detected (MTU &lt; 1500)
                    </div>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-2 text-sm">
                  {results.mtu.commonMTUs.map((mtu, i) => (
                    <div key={i} className={`flex items-center justify-between border rounded p-2 ${
                      mtu.success
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-300 dark:border-gray-700'
                    }`}>
                      <div>
                        <span className="font-medium">{mtu.size}</span>
                        <span className="opacity-70 ml-2">({mtu.name})</span>
                      </div>
                      <div className={mtu.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                        {mtu.success ? '✅' : '❌'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Protocol Info */}
            {results.protocol && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Protocol Details</h3>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm opacity-70">Protocol</div>
                    <div className="text-2xl font-bold">
                      {results.protocol.protocol === 'h2' ? 'HTTP/2' :
                       results.protocol.protocol === 'h3' ? 'HTTP/3' :
                       results.protocol.protocol || 'HTTP/1.1'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm opacity-70">Connection Reuse</div>
                    <div className={`text-2xl font-bold ${
                      results.protocol.connectionReused ? 'text-green-600 dark:text-green-400' : ''
                    }`}>
                      {results.protocol.connectionReused ? 'Yes' : 'No'}
                    </div>
                  </div>
                </div>

                {results.protocol.tlsVersion && (
                  <div className="mt-4 text-sm">
                    <span className="opacity-70">TLS:</span> {results.protocol.tlsVersion}
                  </div>
                )}
              </div>
            )}

            {/* Geographic Routing */}
            {results.geo && results.geo.length > 0 && (
              <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Geographic Routing</h3>

                <div className="space-y-2">
                  {results.geo.map((geo, i) => (
                    <div key={i} className="flex items-center justify-between border border-navy/20 dark:border-border rounded p-3">
                      <div>
                        <div className="font-medium">{geo.endpoint}</div>
                        <div className="text-sm opacity-70">{geo.location}</div>
                      </div>
                      <div className={`text-lg font-bold ${
                        geo.latency < 100 ? 'text-green-600 dark:text-green-400' :
                        geo.latency < 200 ? 'text-yellow-600 dark:text-yellow-400' :
                        'text-red-600 dark:text-red-400'
                      }`}>
                        {geo.latency > 0 ? `${geo.latency.toFixed(2)} ms` : 'Failed'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {results.errors.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-500 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4 text-red-600 dark:text-red-400">Errors</h3>
                <ul className="space-y-1 text-sm text-red-600 dark:text-red-400">
                  {results.errors.map((error, i) => (
                    <li key={i}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Monitor Tab */}
        {activeTab === 'monitor' && (
          <div className="space-y-6">
            <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
              <h2 className="text-2xl font-bold mb-4">Real-Time Connection Monitoring</h2>

              {!monitor?.isActive() ? (
                <div className="space-y-4">
                  <p className="opacity-80">
                    Continuously monitor your connection to detect degradations, outages, and SLA violations in real-time.
                  </p>

                  <div>
                    <label className="block mb-2 text-sm font-medium">Target URL to Monitor</label>
                    <input
                      type="text"
                      value={target}
                      onChange={(e) => setTarget(e.target.value)}
                      placeholder="https://example.com"
                      className="w-full px-4 py-2 bg-white dark:bg-background text-accent-foreground dark:text-foreground border border-navy/20 dark:border-border rounded"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-2 text-sm font-medium">Test Interval (seconds)</label>
                      <input
                        type="number" inputMode="numeric"
                        value={monitorConfig.interval}
                        onChange={(e) => setMonitorConfig({ ...monitorConfig, interval: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 bg-white dark:bg-background text-accent-foreground dark:text-foreground border border-navy/20 dark:border-border rounded"
                        min="5"
                        max="300"
                      />
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium">Latency Threshold (ms)</label>
                      <input
                        type="number" inputMode="numeric"
                        value={monitorConfig.latencyThreshold}
                        onChange={(e) => setMonitorConfig({ ...monitorConfig, latencyThreshold: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 bg-white dark:bg-background text-accent-foreground dark:text-foreground border border-navy/20 dark:border-border rounded"
                        min="50"
                        max="5000"
                      />
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium">Packet Loss Threshold (%)</label>
                      <input
                        type="number" inputMode="numeric"
                        value={monitorConfig.packetLossThreshold}
                        onChange={(e) => setMonitorConfig({ ...monitorConfig, packetLossThreshold: parseFloat(e.target.value) })}
                        className="w-full px-4 py-2 bg-white dark:bg-background text-accent-foreground dark:text-foreground border border-navy/20 dark:border-border rounded"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                    </div>

                    <div className="flex items-center">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={monitorConfig.notificationsEnabled}
                          onChange={(e) => setMonitorConfig({ ...monitorConfig, notificationsEnabled: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm font-medium">Enable Desktop Notifications</span>
                      </label>
                    </div>
                  </div>

                  <button
                    onClick={startMonitoring}
                    className="px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent-hover transition-colors font-medium"
                  >
                    Start Monitoring
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Monitoring Target */}
                  <div className="bg-white dark:bg-background p-4 rounded border border-navy/20 dark:border-border">
                    <div className="text-sm opacity-70 mb-1">Monitoring Target</div>
                    <div className="font-mono text-sm break-all">{target}</div>
                  </div>

                  {/* Monitor Stats */}
                  {monitorStats && (
                    <>
                      <div className="grid md:grid-cols-4 gap-4">
                        <div className="bg-white dark:bg-background p-4 rounded border border-navy/20 dark:border-border">
                          <div className="text-sm opacity-70 mb-1">Uptime</div>
                          <div className={`text-2xl font-bold ${
                            monitorStats.uptime >= 99.9 ? 'text-green-600 dark:text-green-400' :
                            monitorStats.uptime >= 99 ? 'text-yellow-600 dark:text-yellow-400' :
                            'text-red-600 dark:text-red-400'
                          }`}>
                            {monitorStats.uptime.toFixed(2)}%
                          </div>
                        </div>

                        <div className="bg-white dark:bg-background p-4 rounded border border-navy/20 dark:border-border">
                          <div className="text-sm opacity-70 mb-1">Avg Latency</div>
                          <div className="text-2xl font-bold">
                            {monitorStats.avgLatency.toFixed(0)} ms
                          </div>
                        </div>

                        <div className="bg-white dark:bg-background p-4 rounded border border-navy/20 dark:border-border">
                          <div className="text-sm opacity-70 mb-1">Total Tests</div>
                          <div className="text-2xl font-bold">
                            {monitorStats.totalTests}
                          </div>
                        </div>

                        <div className="bg-white dark:bg-background p-4 rounded border border-navy/20 dark:border-border">
                          <div className="text-sm opacity-70 mb-1">Failed Tests</div>
                          <div className={`text-2xl font-bold ${
                            monitorStats.failedTests === 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                          }`}>
                            {monitorStats.failedTests}
                          </div>
                        </div>
                      </div>

                      {/* SLA Status */}
                      {(() => {
                        const slaStatus = calculateSLAStatus(monitorStats, slaConfig);
                        return (
                          <div className={`p-4 rounded border ${
                            slaStatus.overallSLAMet
                              ? 'bg-green-50 dark:bg-green-900/20 border-green-500'
                              : 'bg-red-50 dark:bg-red-900/20 border-red-500'
                          }`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="font-medium">SLA Status</div>
                              <div className={`font-bold ${
                                slaStatus.overallSLAMet ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                              }`}>
                                {slaStatus.overallSLAMet ? '✅ Met' : '❌ Violated'}
                              </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-2 text-sm">
                              <div>
                                Uptime: {slaStatus.currentUptime.toFixed(2)}% / {slaStatus.targetUptime}%
                                {slaStatus.uptimeMet ? ' ✅' : ' ❌'}
                              </div>
                              <div>
                                Latency: {slaStatus.avgLatency.toFixed(0)}ms / {slaStatus.targetLatency}ms
                                {slaStatus.latencyMet ? ' ✅' : ' ❌'}
                              </div>
                            </div>
                            {slaStatus.credits && (
                              <div className="mt-2 text-sm">
                                SLA Credits Earned: {slaStatus.credits}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Event Log */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-lg font-bold">Event Log</h3>
                          <button
                            onClick={() => monitor.clearEvents()}
                            className="text-sm text-accent hover:underline"
                          >
                            Clear
                          </button>
                        </div>
                        <div className="bg-white dark:bg-background rounded border border-navy/20 dark:border-border p-4 max-h-96 overflow-y-auto">
                          {monitorStats.events.length === 0 ? (
                            <div className="text-sm opacity-70 text-center py-4">No events yet</div>
                          ) : (
                            <div className="space-y-2">
                              {monitorStats.events.map((event, i) => (
                                <div key={i} className={`text-sm p-2 rounded ${
                                  event.type === 'error' ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400' :
                                  event.type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400' :
                                  'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                                }`}>
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      {event.type === 'error' ? '❌' : event.type === 'warning' ? '⚠️' : '✅'} {event.message}
                                    </div>
                                    <div className="text-xs opacity-70 whitespace-nowrap">
                                      {new Date(event.timestamp).toLocaleTimeString()}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Controls */}
                  <div className="flex gap-4">
                    <button
                      onClick={stopMonitoring}
                      className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                    >
                      Stop Monitoring
                    </button>
                    <button
                      onClick={exportMonitorReport}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Export Report
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Test History</h2>
                {history.length > 0 && (
                  <button
                    onClick={() => {
                      clearTestHistory();
                      setHistory([]);
                    }}
                    className="text-sm text-red-600 dark:text-red-400 hover:underline"
                  >
                    Clear History
                  </button>
                )}
              </div>

              {history.length === 0 ? (
                <div className="text-center py-12 opacity-70">
                  <p>No test history for this target</p>
                  <p className="text-sm mt-2">Run some tests to see historical data</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {history.map((test) => (
                    <div key={test.id} className="border border-navy/20 dark:border-border rounded p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm opacity-70">
                          {new Date(test.timestamp).toLocaleString()}
                        </div>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        {test.latency && (
                          <div>
                            <span className="opacity-70">Latency:</span> {test.latency.toFixed(2)} ms
                          </div>
                        )}
                        {test.packetLoss !== undefined && (
                          <div>
                            <span className="opacity-70">Packet Loss:</span> {test.packetLoss.toFixed(2)}%
                          </div>
                        )}
                        {test.bandwidth && (
                          <div>
                            <span className="opacity-70">Bandwidth:</span> {test.bandwidth.toFixed(2)} Mbps
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Report Tab */}
        {activeTab === 'report' && (
          <div className="space-y-6">
            <div className="bg-light-surface dark:bg-surface p-6 rounded-lg">
              <h2 className="text-2xl font-bold mb-4">Export Test Results</h2>

              {!results ? (
                <div className="text-center py-12 opacity-70">
                  <p>No test results to export</p>
                  <p className="text-sm mt-2">Run a test first</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="opacity-80">
                    Export your test results for documentation, sharing, or further analysis.
                  </p>

                  <div className="grid md:grid-cols-2 gap-4">
                    <button
                      onClick={exportJSON}
                      className="px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-left"
                    >
                      <div className="font-bold mb-1">Export as JSON</div>
                      <div className="text-sm opacity-80">Machine-readable format for automation</div>
                    </button>

                    <button
                      onClick={exportMarkdown}
                      className="px-6 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-left"
                    >
                      <div className="font-bold mb-1">Export as Markdown</div>
                      <div className="text-sm opacity-80">Human-readable report for documentation</div>
                    </button>
                  </div>

                  <div className="bg-white dark:bg-background p-4 rounded border border-navy/20 dark:border-border">
                    <div className="text-sm font-medium mb-2">Preview (Markdown)</div>
                    <pre className="text-xs opacity-70 overflow-x-auto whitespace-pre-wrap max-h-96">
                      {generateMarkdownReport(results)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
